<template>
  <div>
    <SelectComponent
      v-model="pickedBank"
      :label="$t('attributes.check_issuer_bank')"
      name="issuing_bank"
      :edition-mode="true"
      :values="banks"
      :disabled="locks.pick"
      :errors="errors?.issuing_bank"
    />
  </div>
  <div>
    <BaseShowEditInput
      v-model="typedBank"
      :label="$t('attributes.other_bank_free_text')"
      name="origin_bank_account"
      :edition-mode="true"
      :disabled="locks.type"
      :errors="errors?.origin_bank_account"
    />
    <div
      v-if="showChoice"
      class="mt-1 flex flex-col gap-1 rounded border p-2 text-sm"
      data-test="issuing-bank-choice"
    >
      <p v-if="similar.length">
        {{
          $t('global.similar_bank_found', {
            names: similar.map((bank) => bank.name).join(', ')
          })
        }}
      </p>
      <div class="flex flex-wrap gap-1">
        <BaseButton
          v-for="bank in similar"
          :key="bank.id"
          :title="$t('global.use_known_bank', { name: bank.name })"
          size="extra-small"
          color="primary"
          @click="useBank(bank)"
        />
        <BaseButton
          v-if="canCreate"
          :title="$t('global.add_bank_to_list')"
          :display-loader="creating"
          :disabled="creating"
          size="extra-small"
          color="primary"
          @click="createBank"
        />
        <BaseButton
          v-if="similar.length"
          :title="$t('global.keep_bank_for_this_payment')"
          size="extra-small"
          color="secondary"
          @click="dismissed = true"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import { mapActions, mapGetters } from 'vuex'
import SelectComponent from '@c/BaseFormComponent/SelectComponent.vue'
import BaseButton from '@c/addf-package/components/BaseButton/BaseButton.vue'
import BaseShowEditInput from '@c/addf-package/components/BaseShowEditInput/BaseShowEditInput.vue'
import {
  issuingBankLocks,
  selectableBanks,
  similarBanks,
  withPickedBank,
  withTypedBank
} from '@ui/modal/payment/issuingBank'

const MIN_NEW_BANK_NAME_LENGTH = 3

/**
 * The bank a cheque was issued by: picked in the list of known banks first and, when
 * the bank is not there, typed. Nothing is cleared on the way: once one of the two is
 * used the other is disabled, until the first is emptied. When both are filled (a
 * cheque recorded before the list) nothing is disabled, and the pick is the one shown.
 *
 * Every change is emitted as a new `payment` object (`v-model:payment`): the parent
 * must bind it with `v-model`, not keep a reference to the object it passed in.
 */
export default defineComponent({
  name: 'IssuingBankInput',
  components: { BaseButton, BaseShowEditInput, SelectComponent },
  props: {
    payment: {
      type: Object,
      required: true
    },
    errors: {
      type: [Object, Array],
      required: false,
      default: () => ({})
    }
  },
  emits: ['update:payment'],
  data() {
    return { dismissed: false, creating: false }
  },
  computed: {
    ...mapGetters({ initialMetadata: 'metadata/getInitialMetadata' }),
    banks() {
      return selectableBanks(this.initialMetadata?.models?.banks)
    },
    /**
     * A name typed while no bank is picked: it may already be in the list, or be
     * worth adding to it for the next cheques.
     */
    similar() {
      if (this.payment.issuing_bank) return []
      return similarBanks(this.payment.origin_bank_account, this.banks)
    },
    /** A bank is created only from a name long enough not to pollute the shared list. */
    canCreate() {
      const allowed = this.$can('manage_content') || this.$can('manage_payment')
      return allowed && this.typedName.length >= MIN_NEW_BANK_NAME_LENGTH
    },
    typedName() {
      return (this.payment.origin_bank_account ?? '').trim()
    },
    showChoice() {
      if (!this.typedName || this.payment.issuing_bank || this.dismissed) {
        return false
      }
      return this.similar.length > 0 || this.canCreate
    },
    locks() {
      return issuingBankLocks(this.payment)
    },
    pickedBank: {
      get() {
        return this.payment.issuing_bank ?? null
      },
      set(bankId) {
        this.$emit('update:payment', withPickedBank(this.payment, bankId))
      }
    },
    typedBank: {
      get() {
        return this.payment.origin_bank_account ?? ''
      },
      set(name) {
        this.$emit('update:payment', withTypedBank(this.payment, name))
      }
    }
  },
  watch: {
    'payment.origin_bank_account'() {
      this.dismissed = false
    }
  },
  methods: {
    ...mapActions({ storeNewBank: 'invoicing/storeNewBank' }),
    /** Picks the bank and drops the typed name: the list is now the source. */
    useBank(bank) {
      this.$emit('update:payment', {
        ...withPickedBank(this.payment, bank.id),
        origin_bank_account: null
      })
    },
    async createBank() {
      this.creating = true
      try {
        const bank = await this.storeNewBank({
          name: this.typedName
        })
        if (bank) this.useBank(bank)
      } catch {
        // the HTTP client already toasts the failure; the typed name stays as is
      } finally {
        this.creating = false
      }
    }
  }
})
</script>
